import { observable } from 'mobx';
import type { AutoRemoveFreeePostDeals } from './setting';
import type { RakurakuSettingArgs } from './rakuraku_setting';
import RakurakuSetting from './rakuraku_setting';

export type SettingResponseArgs = {
  id: number;
  purchase_section: number;
  is_internal_number_required: boolean;
  is_project_name_required: boolean;
  is_charging_department_required: boolean;
  is_purpose_business_trip_required: boolean;
  is_destination_business_trip_required: boolean;
  is_expenses_account_type_required: boolean;
  is_trip_report_activity_content_required: boolean;
  is_trip_report_results_content_required: boolean;
  is_trip_report_other_text_required: boolean;
  show_internal_number: boolean;
  show_project_name: boolean;
  show_charging_department: boolean;
  show_expenses_account_type: boolean;
  show_trip_report_activity_content: boolean;
  show_trip_report_results_content: boolean;
  show_trip_report_other_text: boolean;
  show_purpose_business_trip: boolean;
  show_destination_business_trip: boolean;
  financial_last_month: number;
  count_allowance_without_stay: boolean;
  allow_private_use: boolean;
  timeout_minutes: number;
  rakuraku_setting: RakurakuSettingArgs;
  send_user_forwarded_email: boolean;
  disable_show_milage_number: boolean;
  aggregate_unit: string;
  aggregate_base: string;
  expense_tool: string;
  auto_remove_freee_post_deals: AutoRemoveFreeePostDeals;
  default_smart_search_enabled: boolean;
  prevent_zero_amount_application: boolean;
  corporate_published_fare_parallel_display_enabled?: boolean;
};

export type SettingArgs = {
  id: number;
  purchaseSection: number;
  isInternalNumberRequired: boolean;
  isProjectNameRequired: boolean;
  isChargingDepartmentRequired: boolean;
  isPurposeBusinessTripRequired: boolean;
  isDestinationBusinessTripRequired: boolean;
  isExpensesAccountTypeRequired: boolean;
  isTripReportActivityContentRequired: boolean;
  isTripReportResultsContentRequired: boolean;
  isTripReportOtherTextRequired: boolean;
  showInternalNumber: boolean;
  showProjectName: boolean;
  showChargingDepartment: boolean;
  showExpensesAccountType: boolean;
  showTripReportActivityContent: boolean;
  showTripReportResultsContent: boolean;
  showTripReportOtherText: boolean;
  showPurposeBusinessTrip: boolean;
  showDestinationBusinessTrip: boolean;
  financialLastMonth: number;
  countAllowanceWithoutStay: boolean;
  allowPrivateUse: boolean;
  timeoutMinutes: number;
  rakurakuSetting: RakurakuSetting;
  sendUserForwardedEmail: boolean;
  disableShowMilageNumber: boolean;
  aggregateUnit: string;
  aggregateBase: string;
  expenseTool: string;
  autoRemoveFreeePostDeals: AutoRemoveFreeePostDeals;
  defaultSmartSearchEnabled: boolean;
  preventZeroAmountApplication: boolean;
  corporatePublishedFareParallelDisplayEnabled?: boolean;
};

export const convertSettingResponseToArgs = (response: SettingResponseArgs) => {
  const args: SettingArgs = {
    id: response.id,
    purchaseSection: response.purchase_section,
    isInternalNumberRequired: response.is_internal_number_required,
    isProjectNameRequired: response.is_project_name_required,
    isChargingDepartmentRequired: response.is_charging_department_required,
    isPurposeBusinessTripRequired: response.is_purpose_business_trip_required,
    isDestinationBusinessTripRequired: response.is_destination_business_trip_required,
    isExpensesAccountTypeRequired: response.is_expenses_account_type_required,
    isTripReportActivityContentRequired: response.is_trip_report_activity_content_required,
    isTripReportResultsContentRequired: response.is_trip_report_results_content_required,
    isTripReportOtherTextRequired: response.is_trip_report_other_text_required,
    showInternalNumber: response.show_internal_number,
    showProjectName: response.show_project_name,
    showChargingDepartment: response.show_charging_department,
    showExpensesAccountType: response.show_expenses_account_type,
    showTripReportActivityContent: response.show_trip_report_activity_content,
    showTripReportResultsContent: response.show_trip_report_results_content,
    showTripReportOtherText: response.show_trip_report_other_text,
    showPurposeBusinessTrip: response.show_purpose_business_trip,
    showDestinationBusinessTrip: response.show_destination_business_trip,
    financialLastMonth: response.financial_last_month,
    countAllowanceWithoutStay: response.count_allowance_without_stay,
    allowPrivateUse: response.allow_private_use,
    timeoutMinutes: response.timeout_minutes,
    rakurakuSetting: new RakurakuSetting(response.rakuraku_setting),
    sendUserForwardedEmail: response.send_user_forwarded_email,
    disableShowMilageNumber: response.disable_show_milage_number,
    aggregateUnit: response.aggregate_unit,
    aggregateBase: response.aggregate_base,
    expenseTool: response.expense_tool,
    autoRemoveFreeePostDeals: response.auto_remove_freee_post_deals,
    defaultSmartSearchEnabled: response.default_smart_search_enabled,
    preventZeroAmountApplication: response.prevent_zero_amount_application,
    corporatePublishedFareParallelDisplayEnabled:
      response.corporate_published_fare_parallel_display_enabled ?? false
  };
  return args;
};

export default class Setting {
  @observable id: number;
  @observable purchaseSection: number;
  @observable isInternalNumberRequired: boolean;
  @observable isProjectNameRequired: boolean;
  @observable isChargingDepartmentRequired: boolean;
  @observable isPurposeBusinessTripRequired: boolean;
  @observable isDestinationBusinessTripRequired: boolean;
  @observable isExpensesAccountTypeRequired: boolean;
  @observable isTripReportActivityContentRequired: boolean;
  @observable isTripReportResultsContentRequired: boolean;
  @observable isTripReportOtherTextRequired: boolean;
  @observable showInternalNumber: boolean;
  @observable showProjectName: boolean;
  @observable showChargingDepartment: boolean;
  @observable showExpensesAccountType: boolean;
  @observable showTripReportActivityContent: boolean;
  @observable showTripReportResultsContent: boolean;
  @observable showTripReportOtherText: boolean;
  @observable showPurposeBusinessTrip: boolean;
  @observable showDestinationBusinessTrip: boolean;
  @observable financialLastMonth: number;
  @observable countAllowanceWithoutStay: boolean;
  @observable allowPrivateUse: boolean;
  @observable timeoutMinutes: number;
  @observable rakurakuSetting: RakurakuSetting;
  @observable sendUserForwardedEmail: boolean;
  @observable disableShowMilageNumber: boolean;
  @observable aggregateUnit: string;
  @observable aggregateBase: string;
  @observable expenseTool: string;
  @observable autoRemoveFreeePostDeals: AutoRemoveFreeePostDeals;
  @observable defaultSmartSearchEnabled: boolean;
  @observable preventZeroAmountApplication: boolean;
  @observable corporatePublishedFareParallelDisplayEnabled: boolean;

  constructor(args: SettingArgs) {
    this.id = args.id;
    this.purchaseSection = args.purchaseSection;
    this.isInternalNumberRequired = args.isInternalNumberRequired;
    this.isProjectNameRequired = args.isProjectNameRequired;
    this.isChargingDepartmentRequired = args.isChargingDepartmentRequired;
    this.isPurposeBusinessTripRequired = args.isPurposeBusinessTripRequired;
    this.isDestinationBusinessTripRequired = args.isDestinationBusinessTripRequired;
    this.isExpensesAccountTypeRequired = args.isExpensesAccountTypeRequired;
    this.isTripReportActivityContentRequired = args.isTripReportActivityContentRequired;
    this.isTripReportResultsContentRequired = args.isTripReportResultsContentRequired;
    this.isTripReportOtherTextRequired = args.isTripReportOtherTextRequired;
    this.showInternalNumber = args.showInternalNumber;
    this.showProjectName = args.showProjectName;
    this.showChargingDepartment = args.showChargingDepartment;
    this.showExpensesAccountType = args.showExpensesAccountType;
    this.showTripReportActivityContent = args.showTripReportActivityContent;
    this.showTripReportResultsContent = args.showTripReportResultsContent;
    this.showTripReportOtherText = args.showTripReportOtherText;
    this.showPurposeBusinessTrip = args.showPurposeBusinessTrip;
    this.showDestinationBusinessTrip = args.showDestinationBusinessTrip;
    this.financialLastMonth = args.financialLastMonth;
    this.countAllowanceWithoutStay = args.countAllowanceWithoutStay;
    this.allowPrivateUse = args.allowPrivateUse;
    this.timeoutMinutes = args.timeoutMinutes;
    this.rakurakuSetting = args.rakurakuSetting;
    this.sendUserForwardedEmail = args.sendUserForwardedEmail;
    this.disableShowMilageNumber = args.disableShowMilageNumber;
    this.aggregateUnit = args.aggregateUnit;
    this.aggregateBase = args.aggregateBase;
    this.expenseTool = args.expenseTool;
    this.autoRemoveFreeePostDeals = args.autoRemoveFreeePostDeals;
    this.defaultSmartSearchEnabled = args.defaultSmartSearchEnabled;
    this.preventZeroAmountApplication = args.preventZeroAmountApplication;
    this.corporatePublishedFareParallelDisplayEnabled = args.corporatePublishedFareParallelDisplayEnabled ?? false;
  }
}
